import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, Navigate } from 'react-router-dom';
import { Button } from '../../components/Ui';
import TermsSheet from '../../components/TermsSheet';
import { Reveal, Pop } from '../../components/Motion';
import api from '../../api/endpoints';
import { useAuth } from '../../context/AuthContext';
import { colors } from '../../theme';

const LENGTH = 6;

/**
 * OTP verification. In development the server returns the fixed OTP (123456)
 * and it is pre-filled here so testing never needs a real WhatsApp message.
 */
const OtpPage = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const { applyAuth } = useAuth();

  const { phone, role, devOtp, devMode } = state || {};

  const [digits, setDigits] = useState(() => {
    const preset = devMode && devOtp ? String(devOtp).split('') : [];
    return Array.from({ length: LENGTH }, (_, i) => preset[i] || '');
  });
  const [error, setError] = useState('');
  const [accepted, setAccepted] = useState(false);
  const [termsOpen, setTermsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [seconds, setSeconds] = useState(30);
  const inputs = useRef([]);

  useEffect(() => {
    if (seconds <= 0) return undefined;
    const t = setTimeout(() => setSeconds((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [seconds]);

  if (!phone) return <Navigate to="/get-started" replace />;

  const setDigit = (value, index) => {
    const clean = value.replace(/\D/g, '');
    setError('');

    // Pasting the whole code fills every box at once.
    if (clean.length > 1) {
      const next = clean.slice(0, LENGTH).split('');
      setDigits(Array.from({ length: LENGTH }, (_, i) => next[i] || ''));
      const focus = inputs.current[Math.min(next.length, LENGTH - 1)];
      if (focus) focus.focus();
      return;
    }

    const next = [...digits];
    next[index] = clean;
    setDigits(next);
    if (clean && index < LENGTH - 1 && inputs.current[index + 1]) inputs.current[index + 1].focus();
  };

  const onKeyDown = (e, index) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0 && inputs.current[index - 1]) {
      inputs.current[index - 1].focus();
    }
  };

  const verify = async (e) => {
    if (e) e.preventDefault();
    const otp = digits.join('');
    if (otp.length !== LENGTH) { setError('Enter the complete 6 digit OTP'); return; }
    // Nobody signs in without accepting the terms for the side they came in on.
    if (!accepted) { setError('Please accept the Terms & Conditions to continue'); return; }
    setLoading(true);
    setError('');
    try {
      const res = await api.auth.verifyOtp({ phone, otp, role, acceptedTerms: true });
      const data = res.data;

      // New accounts finish their profile; partners then add the business.
      if (!data.partner && (data.isNewUser || !(data.user && data.user.name))) {
        navigate('/profile-setup', { replace: true, state: { authPayload: data, role } });
        return;
      }
      // `role` is the door they came through - it decides which side they land
      // on, whatever the account was upgraded to in the past.
      await applyAuth(data, role);
      if (role === 'partner' && !data.partner) {
        navigate('/partner/register', { replace: true });
        return;
      }
      navigate(role === 'partner' ? '/partner' : '/home', { replace: true });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    try {
      await api.auth.sendOtp({ phone, role });
      setSeconds(30);
      setError('');
    } catch (err) { setError(err.message); }
  };

  return (
    <div style={{ minHeight: '100vh', background: '#fff' }}>
      <div style={{ maxWidth: 460, width: '100%', margin: '0 auto', padding: 22 }}>
        <button
          type="button"
          className="press"
          onClick={() => navigate(-1)}
          aria-label="Go back"
          style={{
            width: 36, height: 36, borderRadius: 12, background: colors.bg,
            border: 0, fontSize: 18, marginBottom: 20,
          }}
        >
          ‹
        </button>

        <Pop>
          <div
            style={{
              width: 76, height: 76, borderRadius: 38, background: colors.successBg,
              display: 'grid', placeItems: 'center', fontSize: 34,
            }}
          >
            💬
          </div>
        </Pop>

        <Reveal delay={80}>
          <h1 className="h1" style={{ marginTop: 20 }}>Verify your number</h1>
          <p className="t-body" style={{ color: colors.muted, marginTop: 8, lineHeight: 1.6 }}>
            We sent a 6 digit code on WhatsApp to
            <br />
            <strong style={{ color: colors.ink }}>+91 {phone}</strong>
          </p>
        </Reveal>

        <form onSubmit={verify}>
          <div className="row" style={{ gap: 9, marginTop: 26 }}>
            {digits.map((d, i) => (
              <input
                // eslint-disable-next-line react/no-array-index-key
                key={i}
                ref={(el) => { inputs.current[i] = el; }}
                value={d}
                onChange={(e) => setDigit(e.target.value, i)}
                onKeyDown={(e) => onKeyDown(e, i)}
                onFocus={(e) => e.target.select()}
                inputMode="numeric"
                autoComplete={i === 0 ? 'one-time-code' : 'off'}
                maxLength={LENGTH}
                aria-label={`OTP digit ${i + 1}`}
                style={{
                  flex: 1, minWidth: 0, height: 56, borderRadius: 12, textAlign: 'center',
                  fontSize: 21, fontWeight: 800, color: colors.ink, outline: 'none',
                  border: `1.4px solid ${error ? colors.danger : d ? colors.primary : colors.line}`,
                  background: d ? colors.primaryLight : '#fff',
                  transition: 'border-color .16s ease, background .16s ease',
                }}
              />
            ))}
          </div>

          {error ? <div className="field-error" style={{ marginTop: 12 }}>{error}</div> : null}

          {devMode ? (
            <div
              style={{
                marginTop: 16, background: colors.accentLight, padding: 12, borderRadius: 12,
                border: '1px dashed #F8D2AE',
              }}
            >
              <span className="t-small" style={{ color: '#9A4B08' }}>
                🧪 Development mode — OTP is always <strong>{devOtp || '123456'}</strong>
              </span>
            </div>
          ) : null}

          <label className="accept-row">
            <input
              type="checkbox"
              checked={accepted}
              onChange={(e) => { setAccepted(e.target.checked); setError(''); }}
            />
            <span>
              I accept the{' '}
              <button type="button" className="terms-link" onClick={() => setTermsOpen(true)}>
                Terms &amp; Conditions
              </button>
              {role === 'partner' ? ' for businesses on KAAMWALA.' : ' of KAAMWALA.'}
            </span>
          </label>

          <Button
            title="Verify & continue"
            type="submit"
            disabled={!accepted}
            loading={loading}
            size="lg"
            full
            style={{ marginTop: 20 }}
          />
        </form>

        <TermsSheet open={termsOpen} role={role} onClose={() => setTermsOpen(false)} />

        <div style={{ textAlign: 'center', marginTop: 20 }}>
          {seconds > 0 ? (
            <span className="t-small">Resend OTP in {seconds}s</span>
          ) : (
            <button type="button" className="link-btn" onClick={resend}>Resend OTP</button>
          )}
        </div>
      </div>
    </div>
  );
};

export default OtpPage;
